import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { authEnabled, isMfaChallenge, signIn, translateCognitoError, type MfaChallenge } from '../../auth/auth';
import { readReturnTo, withReturnTo } from '../../lib/returnTo';
import Turnstile from '../../components/Turnstile';

/** Cognito のエラーオブジェクトからコードを取り出す（実装により code / name のどちらかに入る）。 */
function cognitoErrorCode(err: unknown): string | undefined {
  const e = err as { code?: string; name?: string } | null | undefined;
  return e?.code ?? e?.name;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Turnstile（VITE_TURNSTILE_SITE_KEY 未設定なら常に null のまま＝従来通り captchaToken 無しでログインできる）。
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  // MFA（認証アプリ）のコード入力ステップ。null の間は通常のパスワード入力画面のまま。
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaError, setMfaError] = useState('');

  // 認証未設定（モックモード）ではこの画面自体を表示しない。
  if (!authEnabled) return <Navigate to="/" replace />;

  const signupSuccess = searchParams.get('signup') === 'success';
  const resetSuccess = searchParams.get('reset') === 'success';

  // 復帰先は location.state.from を優先し、無ければクエリ from を見る（新規登録を挟むと
  // state は失われるため、引き続きクエリで引き継がれてきた値にフォールバックする）。
  const from = readReturnTo(location.state, searchParams);

  const goToDestAndReload = () => {
    const dest = from || '/';
    // AuthContext の user state に反映させるため、遷移後にフルリロードする。
    navigate(dest, { replace: true });
    window.location.reload();
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await signIn(email, password, captchaToken ?? undefined);
      if (isMfaChallenge(result)) {
        // MFA 登録済みユーザーのみここに来る。未登録ユーザーはそのままログイン成功する。
        setMfaChallenge(result);
        setBusy(false);
        return;
      }
      goToDestAndReload();
    } catch (err) {
      if (cognitoErrorCode(err) === 'UserNotConfirmedException') {
        navigate(withReturnTo(`/verify?email=${encodeURIComponent(email)}`, from));
        return;
      }
      setError(translateCognitoError(err));
      setBusy(false);
    }
  };

  const onSubmitMfa = async (e: FormEvent) => {
    e.preventDefault();
    if (!mfaChallenge || mfaBusy) return;
    setMfaBusy(true);
    setMfaError('');
    try {
      await mfaChallenge.submitMfaCode(mfaCode);
      goToDestAndReload();
    } catch (err) {
      setMfaError(translateCognitoError(err));
      setMfaBusy(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        {mfaChallenge ? (
          <>
            <h1 className="auth-card__title">二段階認証</h1>
            <p className="auth-card__lead">
              認証アプリに表示されている6桁のコードを入力してください。
            </p>

            <form className="auth-form" onSubmit={onSubmitMfa}>
              <div className="field">
                <label htmlFor="mfaCode">認証コード</label>
                <input
                  id="mfaCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  autoFocus
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                />
              </div>

              {mfaError && <div className="auth-form__error">{mfaError}</div>}

              <button type="submit" className="btn btn--primary" disabled={mfaBusy}>
                {mfaBusy ? '確認中…' : '確認'}
              </button>
            </form>

            <div className="auth-form__links">
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setMfaChallenge(null);
                  setMfaCode('');
                  setMfaError('');
                }}
              >
                やり直す
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className="auth-card__title">ログイン</h1>
            <p className="auth-card__lead">
              VRMA Hub にログインしてアップロードやお気に入りを利用できます。
            </p>

            {signupSuccess && (
              <div className="auth-form__success">登録が完了しました。ログインしてください。</div>
            )}
            {resetSuccess && (
              <div className="auth-form__success">
                パスワードを再設定しました。新しいパスワードでログインしてください。
              </div>
            )}

            <form className="auth-form" onSubmit={onSubmit}>
              <div className="field">
                <label htmlFor="email">メールアドレス</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="password">パスワード</label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <Turnstile onVerify={setCaptchaToken} />

              {error && <div className="auth-form__error">{error}</div>}

              <button type="submit" className="btn btn--primary" disabled={busy}>
                {busy ? '処理中…' : 'ログイン'}
              </button>
            </form>

            <div className="auth-form__links">
              <Link to={withReturnTo('/reset', from)}>パスワードをお忘れですか？</Link>
              <Link to={withReturnTo('/signup', from)}>アカウントをお持ちでない場合は 新規登録</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
